import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Outlet } from "react-router";

import { newId } from "@/utils/ids";

/**
 * The stock being sent, while it is chosen and checked
 * (apps/mobile/src/transfers/draft.tsx): shared by Send stock (choose) and
 * Check before sending, so going back and forth loses nothing.
 *
 * One list of medicines goes to every branch ticked, as the owner decided;
 * the check page can change a branch's amount, or take a medicine off one
 * branch only. Each branch then gets its own transfer.
 *
 * Also used to change a transfer already sent, before it is received: then
 * there is one branch, fixed, and saving edits it.
 */

/** One medicine going: which batch, the size counted in, and how many. */
export interface DraftLine {
  key: string;
  batchId: string;
  productId: string;
  name: string;
  baseUnit: string;
  batchNo: string | null;
  expiryDate: string | null;
  /** The size counted in, when it is not the smallest unit. */
  size: { id: string; name: string; multiplier: number } | null;
  /** How many of that size, or of the smallest unit. */
  count: number;
  qtyBase: number;
}

export interface Draft {
  toBranchIds: string[];
  lines: DraftLine[];
  /**
   * A branch's own amount for a line (in the smallest unit), or null when the
   * line is taken off that branch. Absent: the line as it is.
   */
  perBranch: Record<string, Record<string, number | null>>;
  /** The line open in the card on the choose page, being changed. */
  editingKey: string | null;
  /** One id per branch, made once, so a second click on Send sends once. */
  transferIds: Record<string, string>;
  /** Changing a transfer already sent: its id and where it goes. */
  editing: { transferId: string; toBranchName: string } | null;
  /**
   * What an edited transfer already took of each batch: it is off the shelf,
   * yet still the sender's to move around while it is not received.
   */
  alreadySent: Record<string, number>;
}

const empty = (): Draft => ({
  toBranchIds: [],
  lines: [],
  perBranch: {},
  editingKey: null,
  transferIds: {},
  editing: null,
  alreadySent: {},
});

interface DraftValue extends Draft {
  toggleBranch: (branchId: string) => void;
  /** Add the line, or replace the one with its key. */
  saveLine: (line: Omit<DraftLine, "key"> & { key?: string }) => void;
  removeLine: (key: string) => void;
  openLine: (key: string | null) => void;
  /** A branch's own amount for a line; null takes it off that branch. */
  setForBranch: (branchId: string, key: string, qtyBase: number | null) => void;
  /** How much of a line goes to a branch, or null when it does not. */
  qtyFor: (branchId: string, line: DraftLine) => number | null;
  /** The transfer ids, one per branch ticked, made once. */
  transferIdsFor: (branchIds: string[]) => Record<string, string>;
  /** Start changing a sent transfer, from what it holds now. */
  startEdit: (edit: { transferId: string; toBranchId: string; toBranchName: string; lines: DraftLine[] }) => void;
  clear: () => void;
}

const DraftContext = createContext<DraftValue | null>(null);

export function TransferDraftProvider({ children }: { children: React.ReactNode }) {
  const [draft, setDraft] = useState<Draft>(empty);

  const toggleBranch = useCallback((branchId: string) => {
    setDraft((d) => ({
      ...d,
      toBranchIds: d.toBranchIds.includes(branchId)
        ? d.toBranchIds.filter((b) => b !== branchId)
        : [...d.toBranchIds, branchId],
    }));
  }, []);

  const saveLine = useCallback((line: Omit<DraftLine, "key"> & { key?: string }) => {
    setDraft((d) => {
      const key = line.key ?? newId();
      const saved: DraftLine = { ...line, key };
      const exists = d.lines.some((l) => l.key === key);
      // A line changed on the choose page applies to every branch again.
      const perBranch = Object.fromEntries(
        Object.entries(d.perBranch).map(([branchId, amounts]) => {
          const rest = { ...amounts };
          delete rest[key];
          return [branchId, rest];
        }),
      );
      return {
        ...d,
        lines: exists ? d.lines.map((l) => (l.key === key ? saved : l)) : [...d.lines, saved],
        perBranch,
        editingKey: null,
      };
    });
  }, []);

  const removeLine = useCallback((key: string) => {
    setDraft((d) => ({
      ...d,
      lines: d.lines.filter((l) => l.key !== key),
      editingKey: d.editingKey === key ? null : d.editingKey,
    }));
  }, []);

  const openLine = useCallback((key: string | null) => setDraft((d) => ({ ...d, editingKey: key })), []);

  const setForBranch = useCallback((branchId: string, key: string, qtyBase: number | null) => {
    setDraft((d) => {
      const perBranch = { ...d.perBranch, [branchId]: { ...(d.perBranch[branchId] ?? {}), [key]: qtyBase } };
      // Taken off every branch: off the list altogether.
      const everywhere = d.toBranchIds.every((b) => perBranch[b]?.[key] === null);
      return everywhere ? { ...d, perBranch, lines: d.lines.filter((l) => l.key !== key) } : { ...d, perBranch };
    });
  }, []);

  /**
   * Made in the click that sends, kept in the draft: a second click after a
   * failure sends the same ids, so nothing is sent twice.
   */
  const transferIdsFor = useCallback(
    (branchIds: string[]) => {
      const ids: Record<string, string> = {};
      for (const b of branchIds) ids[b] = draft.transferIds[b] ?? newId();
      setDraft((d) => ({ ...d, transferIds: { ...ids, ...d.transferIds } }));
      return ids;
    },
    [draft.transferIds],
  );

  const startEdit = useCallback(
    (edit: { transferId: string; toBranchId: string; toBranchName: string; lines: DraftLine[] }) => {
      const alreadySent: Record<string, number> = {};
      for (const line of edit.lines) alreadySent[line.batchId] = (alreadySent[line.batchId] ?? 0) + line.qtyBase;
      setDraft({
        ...empty(),
        toBranchIds: [edit.toBranchId],
        lines: edit.lines,
        editing: { transferId: edit.transferId, toBranchName: edit.toBranchName },
        alreadySent,
      });
    },
    [],
  );

  const clear = useCallback(() => setDraft(empty()), []);

  const value = useMemo<DraftValue>(
    () => ({
      ...draft,
      toggleBranch,
      saveLine,
      removeLine,
      openLine,
      setForBranch,
      qtyFor: (branchId, line) => {
        const own = draft.perBranch[branchId]?.[line.key];
        return own === undefined ? line.qtyBase : own;
      },
      transferIdsFor,
      startEdit,
      clear,
    }),
    [draft, toggleBranch, saveLine, removeLine, openLine, setForBranch, transferIdsFor, startEdit, clear],
  );

  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>;
}

/** Around every app page, so the draft lives while the person looks elsewhere. */
export function TransferDraftLayout() {
  return (
    <TransferDraftProvider>
      <Outlet />
    </TransferDraftProvider>
  );
}

export function useTransferDraft(): DraftValue {
  const value = useContext(DraftContext);
  if (!value) throw new Error("useTransferDraft must be used inside a TransferDraftProvider");
  return value;
}

/** "3 × Strip (10)" or "30 tablets", for an amount in the smallest unit. */
export function amountText(
  plural: (word: string, n: number) => string,
  qtyBase: number,
  baseUnit: string,
  size: { name: string; multiplier: number } | null,
): string {
  if (size && size.multiplier > 1 && qtyBase % size.multiplier === 0) {
    const name = size.name.charAt(0).toUpperCase() + size.name.slice(1);
    return `${qtyBase / size.multiplier} × ${name}`;
  }
  return `${qtyBase} ${plural(baseUnit, qtyBase)}`;
}
